import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import styles from './Planner.module.scss';

// Private two-week planner. Tasks live in Netlify Blobs behind the function in
// netlify/functions/planner.mjs, which also checks the password.

const API = '/.netlify/functions/planner';
const TOKEN_KEY = 'planner-token';
const DAYS = 14;
// Sentinel `day` for tasks parked in the "waiting for" column.
const WAITING = 'waiting';

function readToken() {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch (e) {
    return null;
  }
}

function writeToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    // Private mode: stay signed in for this tab only.
  }
}

// Local-time YYYY-MM-DD, so "today" matches the wall clock rather than UTC.
function keyOf(date) {
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${m}-${d}`;
}

function dateOf(key) {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function nextDays() {
  const start = new Date();
  return Array.from({ length: DAYS }, (_, i) => {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
    return { key: keyOf(date), date };
  });
}

function shortDate(key) {
  return dateOf(key).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function byDue(a, b) {
  return (a.due || '9999').localeCompare(b.due || '9999');
}

function newId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

function Lock({ onToken }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const res = await fetch(API, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'Could not sign in');
      onToken(body.token);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <Box component={'main'} className={styles.lock}>
      <h1>Planner</h1>
      <form onSubmit={submit} className={styles.lockForm}>
        <input
          type={'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={'Password'}
          aria-label={'Password'}
          autoFocus
          autoComplete={'current-password'}
        />
        <button type={'submit'} disabled={busy || !password}>
          {busy ? 'Checking…' : 'Unlock'}
        </button>
      </form>
      {error && <p className={styles.error}>{error}</p>}
    </Box>
  );
}

function Task({ task, today, selected, done, onSelect, onComplete, onDragStart, onDragEnd }) {
  const overdue = task.due && task.due < today;
  const classes = [styles.task, selected && styles.selected, done && styles.done]
    .filter(Boolean)
    .join(' ');

  return (
    <li
      className={classes}
      draggable={!done}
      onDragStart={(e) => {
        e.dataTransfer.setData('text/plain', task.id);
        e.dataTransfer.effectAllowed = 'move';
        onDragStart(task.id);
      }}
      onDragEnd={onDragEnd}
    >
      <button
        type={'button'}
        className={styles.check}
        onClick={() => onComplete(task.id)}
        aria-label={`Complete ${task.name}`}
        title={'Mark done'}
      />
      <button
        type={'button'}
        className={styles.taskBody}
        onClick={(e) => {
          // Keep the click from reaching the day cell, which would drop it right back.
          e.stopPropagation();
          onSelect(task.id);
        }}
      >
        <span className={styles.taskName}>{task.name}</span>
        {task.due && (
          <span className={overdue ? styles.overdue : styles.due}>due {shortDate(task.due)}</span>
        )}
      </button>
    </li>
  );
}

export default function Planner() {
  const [token, setToken] = useState(readToken);
  const [tasks, setTasks] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [due, setDue] = useState('');
  const [dragging, setDragging] = useState(null);
  const [over, setOver] = useState(null);
  const [selected, setSelected] = useState(null);
  const [completing, setCompleting] = useState([]);
  // Saves are chained so a slow request can never land after a newer one.
  const saving = useRef(Promise.resolve());
  // Latest tasks, for the delayed removal in complete() to read.
  const latest = useRef(tasks);
  latest.current = tasks;

  const signOut = useCallback(() => {
    writeToken(null);
    setToken(null);
    setTasks(null);
  }, []);

  useEffect(() => {
    if (!token) return;
    fetch(API, { headers: { authorization: `Bearer ${token}` } })
      .then(async (res) => {
        if (res.status === 401) return signOut();
        const body = await res.json();
        if (!res.ok) throw new Error(body.error || 'Could not load tasks');
        setTasks(body.tasks);
      })
      .catch((err) => setError(err.message));
  }, [token, signOut]);

  function save(next) {
    setTasks(next);
    saving.current = saving.current.then(async () => {
      try {
        const res = await fetch(API, {
          method: 'PUT',
          headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
          body: JSON.stringify({ tasks: next }),
        });
        if (res.status === 401) return signOut();
        if (!res.ok) throw new Error('Could not save — your last change may be lost');
        setError('');
      } catch (err) {
        setError(err.message);
      }
    });
  }

  if (!token) {
    return (
      <Lock
        onToken={(t) => {
          writeToken(t);
          setToken(t);
        }}
      />
    );
  }

  if (!tasks) {
    return (
      <Box component={'main'} className={styles.planner}>
        <h1>Planner</h1>
        <p className={error ? styles.error : styles.muted}>{error || 'Loading…'}</p>
      </Box>
    );
  }

  const days = nextDays();
  const today = days[0].key;
  const visible = new Set(days.map((d) => d.key));
  // Anything scheduled for a day that has scrolled out of view goes back to
  // the tray to be re-planned, rather than silently vanishing.
  const placed = (t) => t.day === WAITING || (t.day && visible.has(t.day));
  const unassigned = tasks.filter((t) => !placed(t)).sort(byDue);
  const waiting = tasks.filter((t) => t.day === WAITING);

  function add(e) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    save([...tasks, { id: newId(), name: trimmed, due: due || null, day: null }]);
    setName('');
    setDue('');
  }

  function move(id, day) {
    const task = tasks.find((t) => t.id === id);
    if (!task || task.day === day) return;
    // Re-append so a task dropped onto a day lands at the bottom of it.
    save([...tasks.filter((t) => t.id !== id), { ...task, day }]);
  }

  function complete(id) {
    if (completing.includes(id)) return;
    setCompleting((c) => [...c, id]);
    if (selected === id) setSelected(null);
    // Let the strike-through play before the task leaves.
    setTimeout(() => {
      setCompleting((c) => c.filter((x) => x !== id));
      save(latest.current.filter((t) => t.id !== id));
    }, 350);
  }

  function zone(day) {
    const zoneKey = day || 'tray';
    return {
      onDragOver: (e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (over !== zoneKey) setOver(zoneKey);
      },
      onDragLeave: (e) => {
        if (!e.currentTarget.contains(e.relatedTarget)) setOver(null);
      },
      onDrop: (e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData('text/plain') || dragging;
        setOver(null);
        setDragging(null);
        if (id) move(id, day);
      },
      // Tap a task, then tap where it should go — drag-free fallback for touch.
      onClick: () => {
        if (!selected) return;
        move(selected, day);
        setSelected(null);
      },
    };
  }

  function zoneClass(base, zoneKey) {
    return [base, over === zoneKey && styles.over, selected && styles.targetable]
      .filter(Boolean)
      .join(' ');
  }

  function renderTask(task) {
    return (
      <Task
        key={task.id}
        task={task}
        today={today}
        selected={selected === task.id}
        done={completing.includes(task.id)}
        onSelect={(id) => setSelected(selected === id ? null : id)}
        onComplete={complete}
        onDragStart={(id) => {
          setDragging(id);
          setSelected(null);
        }}
        onDragEnd={() => {
          setDragging(null);
          setOver(null);
        }}
      />
    );
  }

  return (
    <Box component={'main'} className={styles.planner}>
      <Box className={styles.header}>
        <h1>Planner</h1>
        <button type={'button'} className={styles.signOut} onClick={signOut}>
          sign out
        </button>
      </Box>

      <form onSubmit={add} className={styles.addForm}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder={'New task'}
          aria-label={'Task name'}
          maxLength={200}
        />
        <input
          type={'date'}
          value={due}
          onChange={(e) => setDue(e.target.value)}
          aria-label={'Due date'}
        />
        <button type={'submit'} disabled={!name.trim()}>
          Add
        </button>
      </form>

      {error && <p className={styles.error}>{error}</p>}

      <section className={zoneClass(styles.tray, 'tray')} {...zone(null)}>
        <h2>Unassigned</h2>
        {unassigned.length === 0 ? (
          <p className={styles.muted}>Nothing waiting. Add a task above.</p>
        ) : (
          <ul className={styles.trayList}>{unassigned.map(renderTask)}</ul>
        )}
      </section>

      {selected && (
        <p className={styles.hint}>
          Tap a day or the waiting list to move the selected task, or tap it again to cancel.
        </p>
      )}

      <Box className={styles.board}>
        <Box className={styles.grid}>
          {days.map(({ key, date }) => (
            <section
              key={key}
              className={zoneClass(
                [styles.day, key === today && styles.today].filter(Boolean).join(' '),
                key,
              )}
              {...zone(key)}
            >
              <h3 className={styles.dayHead}>
                <span className={styles.weekday}>
                  {date.toLocaleDateString('en-US', { weekday: 'short' })}
                </span>
                <span className={styles.dayNum}>{shortDate(key)}</span>
              </h3>
              <ul className={styles.dayList}>
                {tasks.filter((t) => t.day === key).map(renderTask)}
              </ul>
            </section>
          ))}
        </Box>

        <section className={zoneClass(styles.waiting, WAITING)} {...zone(WAITING)}>
          <h2>Things I'm Waiting For</h2>
          {waiting.length === 0 ? (
            <p className={styles.muted}>Drop tasks here that are blocked on someone else.</p>
          ) : (
            <ul className={styles.dayList}>{waiting.map(renderTask)}</ul>
          )}
        </section>
      </Box>
    </Box>
  );
}
